import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import { formatDate, formatDateDDMMYYYY, formatCurrency } from "@/lib/utils";
import { Transaction } from "@/types/transaction";
import { TransactionTypeBadge } from "./transaction-type-badge";
import { TransactionStatusBadge } from "./transaction-status-badge";

interface TransactionDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: Transaction | null;
  onViewDocument: (transaction: Transaction) => void;
}

export function TransactionDetailSheet({
  open,
  onOpenChange,
  transaction,
  onViewDocument,
}: TransactionDetailSheetProps) {
  const navigate = useNavigate();
  
  if (!transaction) return null;

  const hasLineItems = transaction.lineItems && transaction.lineItems.length > 0;
  const hasTaxColumn = hasLineItems && transaction.lineItems.some((item) => item.taxAmount);
  const hasDiscountColumn = hasLineItems && transaction.lineItems.some((item) => item.discountAmount);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Transaction Details</SheetTitle>
          <SheetDescription>
            Complete information for {transaction.referenceNumber}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] mt-6">
          <ScrollAreaViewport className="h-full w-full">
            <div className="space-y-6 px-4">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Reference Number</p>
                  <p className="font-medium">{transaction.referenceNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Transaction ID</p>
                  <p className="font-medium">{transaction.id}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Transaction Date</p>
                  <p className="font-medium">{formatDateDDMMYYYY(transaction.transactionDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Customer Name</p>
                  <button
                    onClick={() => navigate(`/customers/${transaction.customerId}`)}
                    className="text-primary hover:underline font-medium transition-colors text-left"
                    title={`View details for ${transaction.customerName}`}
                  >
                    {transaction.customerName}
                  </button>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Transaction Type</p>
                  <div className="mt-1">
                    <TransactionTypeBadge type={transaction.transactionType} />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="mt-1">
                    <TransactionStatusBadge status={transaction.status} />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Amount</p>
                  <p className="text-xl font-bold">
                    {formatCurrency(transaction.amount, transaction.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Currency</p>
                  <p className="font-medium">{transaction.currency}</p>
                </div>
                {transaction.dueDate && (
                  <div>
                    <p className="text-sm text-muted-foreground">Due Date</p>
                    <p className="font-medium">{formatDateDDMMYYYY(transaction.dueDate)}</p>
                  </div>
                )}
                {transaction.paymentMethod && (
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Method</p>
                    <p className="font-medium">{transaction.paymentMethod}</p>
                  </div>
                )}
                {transaction.invoiceNumber && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {transaction.transactionType === "receipt" ? "Applied to Invoice" : "Invoice Number"}
                    </p>
                    <p className="font-medium">{transaction.invoiceNumber}</p>
                  </div>
                )}
                {transaction.reasonCode && (
                  <div>
                    <p className="text-sm text-muted-foreground">Reason Code</p>
                    <p className="font-medium">{transaction.reasonCode}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Line Items */}
            {hasLineItems && (
              <>
                <Separator />
                <div>
                  <h3 className="text-lg font-semibold mb-4">Line Items</h3>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Line #</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Unit Price</TableHead>
                          <TableHead className="text-right">Subtotal</TableHead>
                          {hasTaxColumn && <TableHead className="text-right">Tax</TableHead>}
                          {hasDiscountColumn && <TableHead className="text-right">Discount</TableHead>}
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transaction.lineItems!.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.lineNumber}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(item.unitPrice, transaction.currency)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(item.subtotal, transaction.currency)}
                            </TableCell>
                            {hasTaxColumn && (
                              <TableCell className="text-right">
                                {item.taxAmount
                                  ? formatCurrency(item.taxAmount, transaction.currency)
                                  : "-"}
                              </TableCell>
                            )}
                            {hasDiscountColumn && (
                              <TableCell className="text-right">
                                {item.discountAmount
                                  ? formatCurrency(item.discountAmount, transaction.currency)
                                  : "-"}
                              </TableCell>
                            )}
                            <TableCell className="text-right font-bold">
                              {formatCurrency(item.total, transaction.currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell
                            colSpan={
                              5 + (hasTaxColumn ? 1 : 0) + (hasDiscountColumn ? 1 : 0)
                            }
                            className="text-right font-semibold"
                          >
                            Total
                          </TableCell>
                          <TableCell className="text-right font-bold text-lg">
                            {formatCurrency(
                              transaction.lineItems!.reduce((sum, item) => sum + item.total, 0),
                              transaction.currency
                            )}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                </div>
              </>
            )}

            {/* Description */}
            <Separator />
            <div>
              <h3 className="text-lg font-semibold mb-2">Description</h3>
              <p className="text-sm">{transaction.description}</p>
            </div>

            {/* Notes */}
            {transaction.notes && (
              <>
                <Separator />
                <div>
                  <h3 className="text-lg font-semibold mb-2">Notes</h3>
                  <p className="text-sm">{transaction.notes}</p>
                </div>
              </>
            )}

            {/* Audit Information */}
            <Separator />
            <div>
              <h3 className="text-lg font-semibold mb-4">Audit Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Created By</p>
                  <p className="font-medium">{transaction.createdBy}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDateDDMMYYYY(transaction.createdAt)}
                  </p>
                </div>
                {transaction.modifiedBy && (
                  <div>
                    <p className="text-sm text-muted-foreground">Modified By</p>
                    <p className="font-medium">{transaction.modifiedBy}</p>
                    {transaction.modifiedAt && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDateDDMMYYYY(transaction.modifiedAt)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        {/* Footer Actions */}
        <SheetFooter className="absolute bottom-0 left-0 right-0 p-6 bg-background border-t flex gap-2">
          {transaction.documentUrl && (
            <Button onClick={() => onViewDocument(transaction)} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              View Document
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
